import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Button, Col, Container, Progress, Row } from 'reactstrap';
import dayjs, { Dayjs } from 'dayjs';

import { dateInputFieldFormat, isInSameOrAfterWeek } from '~libs/dayjs';
import { buildAssignChange } from '~weekly-planner/lib/simulate';

import WeekPicker from '~components/WeekPicker';

import { useLazyGetUnallocatedAssignableQuery } from '~weekly-planner/actions/appointments/unallocated';
import { simulate } from '~weekly-planner/actions/simulate';

import { makeChange } from '~weekly-planner/reducers/unsaved';
import { updatePublishDate } from '~weekly-planner/reducers/weeklyPlanner';

import { selectCompanySettings } from '~main/selectors/login';
import { selectDerivedWeekRange, selectPlannerAPIOptions, selectPublishDate } from '~weekly-planner/selectors';
import { selectAllSimulatedChanges } from '~weekly-planner/selectors/simulate';

const AutoAllocations: React.FC = () => {
  const dispatch = useAppDispatch();

  const settings = useAppSelector(selectCompanySettings);
  const plannerAPIOptions = useAppSelector(selectPlannerAPIOptions);

  const data = useAppSelector(selectAllSimulatedChanges);
  const publishDate = useAppSelector(selectPublishDate);
  const { week_start } = useAppSelector(selectDerivedWeekRange);

  const [importDate, setImportDate] = useState<string>(week_start);
  const [importError, setImportError] = useState<boolean>(false);
  const [importMessage, setImportMessage] = useState<string>('');

  useEffect(() => {
    if (!publishDate) {
      const currentPublishDate = settings.published_date as string;
      dispatch(updatePublishDate(currentPublishDate));
    }
  }, []);

  useEffect(() => {
    onDateChange(dayjs(importDate));
  }, [week_start]);

  const onDateChange = async (value: Dayjs) => {
    setImportDate(value.format(dateInputFieldFormat));
    setImportError(isInSameOrAfterWeek(value, week_start));
  };

  const [getAssignableAppts, { isFetching: isImporting }] = useLazyGetUnallocatedAssignableQuery();

  const onImport = async () => {
    setImportMessage('Importing');
    const response = await getAssignableAppts({
      week_start,
      assignable_week_start: importDate,
    }).unwrap();

    if (response?.length) {
      setImportMessage('Simulating');

      const assignChange = buildAssignChange(data, response);
      const changes = Array.isArray(assignChange.data) ? assignChange.data : [assignChange.data];
      await dispatch(simulate({ ...assignChange, week_start, options: plannerAPIOptions }));

      changes.forEach((appt) => {
        dispatch(
          makeChange({
            original: appt,
            change: {
              id: appt.id ?? null,
              parent_repeat_id: appt.parent_repeat_id ?? null,
              user_id: appt.user_id,
              date: appt.date,
            },
          }),
        );
      });
    }

    setImportMessage('');
  };

  return (
    <Container className="card-container">
      <div className="allocation-container">
        <Row className="content">
          <Row>
            <Col sm={12} className="row-header">
              Import Allocations From:
            </Col>
          </Row>
          <Row className="row-center">
            <Col sm={12} className="d-flex justify-content-center">
              <WeekPicker onChange={(value) => onDateChange(value)} showQuickIcons={false} showSelected={false} />
            </Col>
            {importError && (
              <Col sm={12} className="text-danger text-center">
                Selected week can only be in the past!
              </Col>
            )}
          </Row>
          <Row className="row-center">
            <Button size="sm" className="import" onClick={onImport} disabled={importError || isImporting}>
              Import
            </Button>
            <small>* Allocations will only apply to unallocated appts</small>
          </Row>
          {isImporting && (
            <Row>
              <Progress className="text-center" color="success" style={{ height: '25px' }} value={100} animated>
                {importMessage}
              </Progress>
            </Row>
          )}
        </Row>
      </div>
    </Container>
  );
};

export default AutoAllocations;
